/**
 * C:\Sovereign\Projects\Memorials\memorial-intake\usePresignedUpload.ts
 *
 * usePresignedUpload
 *
 * Returns an `uploadFile` function that:
 *   1. Calls the Worker's /api/upload/presign to get a pre-signed PUT URL
 *   2. PUTs the file bytes directly to Cloudflare R2 via XHR (for progress events)
 *   3. Returns the objectKey for storage in form state
 *
 * Worker presign response contract (worker/src/routes/upload.ts):
 *   { url, objectKey, tenant, bucket, expiresIn }
 *
 * ZERO tRPC. ZERO Express. Calls the deployed Cloudflare Worker directly.
 * The hook is stateless — callers manage their own asset state.
 */

import { useCallback } from "react";
import { getPresignedUrl, AssetType } from "./src/lib/api";

export type { AssetType };

export interface UploadResult {
  objectKey: string;  // R2 object key: <tenant>/<projectId>/<fileCategory>/<filename>
}

export function usePresignedUpload(projectId: string) {
  const uploadFile = useCallback(
    async (
      file: File,
      assetType: AssetType,
      onProgress?: (pct: number) => void
    ): Promise<UploadResult> => {
      if (!projectId) throw new Error("projectId is required before uploading");

      // 1. Get pre-signed PUT URL from the Worker
      const { url, objectKey } = await getPresignedUrl({
        projectId,
        filename:     file.name,
        contentType:  file.type || "application/octet-stream",
        fileCategory: assetType,
      });

      // 2. PUT bytes directly to R2 via XHR (for real upload progress events)
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        if (onProgress) {
          xhr.upload.addEventListener("progress", (e) => {
            if (e.lengthComputable) {
              onProgress(Math.round((e.loaded / e.total) * 100));
            }
          });
        }

        xhr.addEventListener("load", () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
          } else {
            reject(new Error(`R2 upload failed: ${xhr.status} ${xhr.statusText}`));
          }
        });

        xhr.addEventListener("error", () =>
          reject(new Error("Network error during R2 upload"))
        );
        xhr.addEventListener("abort", () =>
          reject(new Error("Upload aborted"))
        );

        xhr.open("PUT", url);
        xhr.setRequestHeader("Content-Type", file.type || "application/octet-stream");
        xhr.send(file);
      });

      return { objectKey };
    },
    [projectId]
  );

  return { uploadFile };
}
